import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query";

export type BambiBaseQueryConfig = {
  baseUrl: string;
  /** Resolves the bearer token at request time. Omit for unauthenticated
   *  consumers (e.g. the public booking portal). */
  getAuthToken?: () => Promise<string | null | undefined>;
  /** Resolves the current org id at request time. Omit for consumers that
   *  don't operate in an org context. */
  getOrgId?: () => string | null | undefined;
};

/**
 * Builds a baseQuery that injects auth/org headers per request using the
 * consumer-supplied getters. Consumers that don't supply a getter simply
 * don't send that header — `Organization-ID` is emitted as optional in
 * generated arg types, so per-call overrides remain available for the
 * rare callers that need to act on behalf of a different org.
 */
export function createBambiBaseQuery(
  config: BambiBaseQueryConfig,
): BaseQueryFn<FetchArgs, unknown, FetchBaseQueryError> {
  return fetchBaseQuery({
    baseUrl: config.baseUrl,
    prepareHeaders: async (headers) => {
      // Per-call overrides win — only inject the consumer's getter
      // values when the request didn't already set the header. This
      // matches the comment in BambiBaseQueryConfig and lets callers
      // act on behalf of a different org / token at the endpoint level.
      if (!headers.has("Organization-ID")) {
        const orgId = config.getOrgId?.();
        if (orgId) headers.set("Organization-ID", orgId);
      }
      if (!headers.has("Authorization")) {
        const token = await config.getAuthToken?.();
        if (token) headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  });
}
